// toc.js — build an in-page table of contents from a document's own headings,
// and mark the one being read.
//
// methodology.html is eleven sections long and had no way to move between them:
// no list at the top, no anchors on the headings, so the only navigation was
// the scrollbar. accessibility.html and datenschutz.html are longer still. A
// long document without a contents list is not a styling problem, it is a
// document you cannot use as a reference — you can only read it start to end.
//
// WHY IT IS BUILT HERE RATHER THAN WRITTEN INTO THE MARKUP. The headings carry
// data-i18n keys and the English variants are pre-rendered from the same
// markup, so a hand-written list would be a second copy of every heading in
// two locale files, and it would go stale the first time a section is renamed.
// Reading the headings that are actually in the page cannot go stale, and the
// text is whatever i18n already resolved.
//
// The ids are generated from the heading text, so they are stable for a given
// heading and change if it is renamed — which is the honest behaviour for a
// link into a document whose sections are still moving. A heading that already
// has an id keeps it, so anything already linked from elsewhere still resolves.
(function () {
  var mount = document.querySelector('[data-toc]');
  if (!mount) return;

  // The scope to read headings from, and which level counts as a section.
  var scope = document.querySelector(mount.getAttribute('data-toc') || 'main') || document;
  var headings = [].slice.call(scope.querySelectorAll('h2'));
  if (headings.length < 3) return;   // a list of two is not worth the space

  function slug(text) {
    return text
      .toLowerCase()
      .replace(/[ä]/g, 'ae').replace(/[ö]/g, 'oe').replace(/[ü]/g, 'ue')
      .replace(/[ß]/g, 'ss')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      // An id may legally start with a digit in HTML and may NOT in a CSS
      // selector: `#1-verantwortlicher` throws in querySelector, so anything
      // that later looks the section up by selector breaks on it. Numbered
      // headings are exactly the case that produces one — datenschutz counts
      // its sections — so a leading digit gets a prefix.
      .replace(/^(?=\d)/, 'abschnitt-')
      .slice(0, 60);
  }

  // A heading inside a <details> is invisible until the section is opened, and
  // an anchor that scrolls to something collapsed lands on the summary rather
  // than the content — so opening it is part of following the link.
  function reveal(el) {
    for (var p = el.parentElement; p; p = p.parentElement) {
      if (p.tagName === 'DETAILS' && !p.open) p.open = true;
    }
  }

  var list = document.createElement('ul');
  list.className = 'ui-toc__links';

  var seen = new Set();
  var entries = headings.map(function (h) {
    // The badge span inside methodology's longitudinal heading ("Geplant für
    // v2.0") is part of the heading's text but not part of its name.
    var label = (h.getAttribute('data-toc-label') || h.textContent || '').trim();

    // Prefer an id that already exists. Every section on these pages is a
    // <details class="content-section" id="..."> and those ids are already
    // linked from elsewhere in the site, so inventing a second id on the
    // heading inside would create a parallel set of anchors that mean the same
    // thing — and jumping to the heading rather than to the section is also
    // the wrong target, because the section is what has to be opened.
    // The SECTION's id, not just any ancestor's. `closest('[id]')` walked
    // straight past a heading whose own section has no id and landed on
    // <main id="main"> — so the first entry on datenschutz pointed at the top
    // of the document instead of at its section. Only a real section counts.
    var section = h.closest('details, .content-section');
    var target = (section && section.id) ? section : h;
    if (!target.id) {
      var base = slug(label) || 'abschnitt';
      var id = base;
      var n = 2;
      while (seen.has(id) || document.getElementById(id)) { id = base + '-' + n++; }
      target.id = id;
    }
    seen.add(target.id);

    var li = document.createElement('li');
    var a = document.createElement('a');
    a.className = 'ui-toc__link';
    a.href = '#' + target.id;
    a.textContent = label;
    a.addEventListener('click', function () { reveal(h); });
    li.appendChild(a);
    list.appendChild(li);
    return { heading: h, link: a };
  });

  // The title is written in the markup with its own data-i18n key rather than
  // built here — the locale files are the only place user-visible strings are
  // allowed to live, and a string created in JS would be invisible to both
  // i18n-parity.sh and the English pre-render.
  mount.appendChild(list);

  // The link text is a copy of a heading, so it goes stale the moment the
  // reader switches language: the headings are re-translated in place and the
  // list beside them still says what they used to. Re-read them instead of
  // rebuilding — the ids, the observer and the current mark all stay valid,
  // only the words change.
  //
  // On `i18n-applied` rather than `langchange`, for the reason js/einreichen.js
  // and js/dashboard.js both record: langchange fires BEFORE loadLocale
  // resolves, so reading the headings then gives the previous language back.
  document.addEventListener('i18n-applied', function () {
    entries.forEach(function (e) {
      var label = (e.heading.getAttribute('data-toc-label') || e.heading.textContent || '').trim();
      if (label) e.link.textContent = label;
    });
  });

  // ── Scrollspy ──────────────────────────────────────────────────────────
  // IntersectionObserver rather than a scroll handler: the browser reports
  // the crossings itself, so there is no listener running on every frame and
  // nothing to throttle. The top margin matches the sticky nav, so a heading
  // counts as reached when it clears the nav rather than when it touches the
  // viewport edge and is still hidden behind it.
  if (!('IntersectionObserver' in window)) return;

  var current = null;
  function mark(entry) {
    if (current === entry) return;
    if (current) current.link.removeAttribute('aria-current');
    current = entry;
    if (current) current.link.setAttribute('aria-current', 'true');
  }

  var visible = [];
  var observer = new IntersectionObserver(function (records) {
    records.forEach(function (r) {
      var i = visible.indexOf(r.target);
      if (r.isIntersecting && i === -1) visible.push(r.target);
      if (!r.isIntersecting && i !== -1) visible.splice(i, 1);
    });
    if (!visible.length) return;
    // Topmost of the headings currently in the band is the one being read.
    var top = visible.reduce(function (a, b) {
      return a.getBoundingClientRect().top <= b.getBoundingClientRect().top ? a : b;
    });
    for (var i = 0; i < entries.length; i++) {
      if (entries[i].heading === top) { mark(entries[i]); break; }
    }
  }, { rootMargin: '-80px 0px -70% 0px', threshold: 0 });

  entries.forEach(function (e) { observer.observe(e.heading); });
})();
